import React, { createContext, useEffect, useMemo, useState } from "react";

export const ContentContext = createContext(null);

function flatten(node) {
  const out = [];

  for (const f of node.files || []) {
    out.push({
      ...f,
      id: encodeURIComponent(f.path),
      nameLower: (f.name || "").toLowerCase(),
      pathLower: (f.path || "").toLowerCase(),
      tags: f.tags || [],
    });
  }

  for (const folder of node.folders || []) {
    out.push(...flatten(folder));
  }

  return out;
}

export function ContentProvider({ children }) {
  const [index, setIndex] = useState(null);
  const [query, setQuery] = useState("");
  const [tagFilter, setTagFilter] = useState("ALL"); 

  useEffect(() => {
    fetch("/resumos/index.json")
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("index.json"))))
      .then(setIndex)
      .catch(() => setIndex({ generatedAt: null, root: { folders: [], files: [] } }));
  }, []);

  const allFiles = useMemo(() => (index?.root ? flatten(index.root) : []), [index]);

  const allTags = useMemo(() => {
    const s = new Set();
    for (const f of allFiles) for (const t of f.tags || []) s.add(t);
    return ["ALL", ...Array.from(s).sort((a, b) => a.localeCompare(b))];
  }, [allFiles]);

  const filteredFiles = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allFiles.filter((f) => {
      const matchText =
        !q || f.nameLower.includes(q) || f.pathLower.includes(q) || (f.title || "").toLowerCase().includes(q);

      const matchTag = tagFilter === "ALL" || (f.tags || []).includes(tagFilter);

      return matchText && matchTag;
    });
  }, [allFiles, query, tagFilter]);

  const value = useMemo(
    () => ({
      index,
      query,
      setQuery,
      tagFilter,
      setTagFilter,
      allTags,
      allFiles,
      filteredFiles,
    }),
    [index, query, tagFilter, allTags, allFiles, filteredFiles]
  );

  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
}
